import React, { useEffect, useRef, useState } from "react";
import { DataGrid, useGridApiRef } from "@mui/x-data-grid";
import { SimCardDownload, FileUploadOutlined } from "@mui/icons-material";
import { Box, Button, Checkbox, CircularProgress, ListItemText, Menu, MenuItem, Autocomplete, TextField } from "@mui/material";

import { DateRangeInput, FilterOptions, SearchBar } from ".";

/**
 * A Material-UI DataGrid with optional search bar, filter options, and export button.
 *
 * @param {Object} props - The component props.
 * @param {string} [props.selectedFilter] - The currently selected filter.
 * @param {Array<any>} [props.filterOptions] - The available filter options.
 * @param {function} [props.onFilterChange] - Callback function to call when the filter changes.
 * @param {boolean} [props.showFilterButton] - Whether to show the filter button.
 * @param {boolean} [props.disableFilterButton] - Whether the filter button is disabled (still visible).
 * @param {string} [props.placeholder] - The placeholder for the search bar.
 * @param {string} [props.searchText] - The current search text.
 * @param {function} [props.onSearchChange] - Callback function to call when the search text changes.
 * @param {boolean} [props.showSearchBar] - Whether to show the search bar.
 * @param {boolean} [props.disableSearchBar] - Whether the search bar is disabled (still visible).
 * @param {Array<Object>} [props.dropdownFilters] - Dropdown filters config for toolbar selects.
 * @param {import("react").ReactNode} [props.startAction] - Content rendered at the far left of the toolbar, before the filter options.
 * @param {boolean} [props.showDateRangePicker] - Whether to show the date range picker.
 * @param {Object} [props.dateRange] - Date range passed to the picker. Expects `{ startDate, endDate }`.
 * @param {function} [props.onDateRangeChange] - Callback function to call when the date range changes.
 * @param {string} [props.buttonText] - The text for the export button.
 * @param {boolean} [props.showExportButton] - Whether to show the export button.
 * @param {boolean} [props.isPdfGenerating] - Whether a PDF is currently being generated.
 * @param {boolean} [props.pdfSuccess] - Whether the PDF generation was successful.
 * @param {Object} [props.currentFilters] - The current filters applied to the table.
 * @param {Object} [props.prevFilters] - The previous filters applied to the table.
 * @param {function} [props.onExportPdfClick] - Callback function to call when the export button is clicked.
 * @param {Array<any>} [props.columnHeaders] - The column headers for the table.
 * @param {Array<any>} props.tableRows - The rows for the table.
 * @param {number} [props.rowCount] - The total number of rows.
 * @param {boolean} [props.loading] - Whether the component is in a loading state.
 * @param {Object} [props.sortModel] - The current sort state, including field and sort direction.
 * @param {Object} [props.defaultSortModel] - The default sort state, including field and sort direction.
 * @param {function} [props.onSortChange] - Callback function when the sort changes.
 * @param {boolean} [props.disableSorting] - Whether column sorting is disabled entirely (headers stay visible).
 * @param {Object} [props.paginationModel] - The current pagination state, including page and pageSize.
 * @param {function} [props.onPaginationChange] - Callback function when the pagination changes.
 * @param {import("@mui/x-data-grid").GridEventListener<"rowClick">} [props.onRowClick] - Callback function when a row in the table is clicked.
 * @param {boolean} [props.hideFooter] - Whether to hide the footer.
 * @param {boolean} [props.autoHeight] - Whether the grid should size to its content so the footer hugs the last row.
 * @param {Object} [props.style] - The style for the component.
 */
export const MTable = ({
  selectedFilter,
  filterOptions,
  onFilterChange = () => {},
  showFilterButton = false,
  disableFilterButton = false,
  placeholder = "Search",
  searchText,
  onSearchChange = () => {},
  showSearchBar = false,
  disableSearchBar = false,
  dropdownFilters = [],
  startAction = null,
  showDateRangePicker = false,
  dateRange = {},
  onDateRangeChange = () => {},
  buttonText,
  showExportButton = false,
  isPdfGenerating = false,
  pdfSuccess = false,
  currentFilters = {},
  prevFilters = {},
  onExportPdfClick,
  columnHeaders = [],
  tableRows,
  rowCount = 0,
  loading = false,
  sortModel = [],
  defaultSortModel = [{ field: "updatedAt", sort: "desc" }],
  onSortChange = () => {},
  disableSorting = false,
  paginationModel,
  onPaginationChange = () => {},
  onRowClick = () => {},
  hideFooter = false,
  autoHeight = false,
  style = {},
}) => {
  const [rows, setRows] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const hasDropdownFilters = Array.isArray(dropdownFilters) && dropdownFilters.length > 0;
  const hasStartAction = Boolean(startAction);

  const apiRef = useGridApiRef();
  const lastSortRef = useRef(sortModel);

  useEffect(() => {
    setRows(tableRows);
  }, [tableRows]);

  const normalizeSortModel = (model) => (model.length === 0 ? defaultSortModel : model);
  const isSameSort = (a = [], b = []) => a.length === b.length && a[0]?.field === b[0]?.field && a[0]?.sort === b[0]?.sort;

  const handleSortModelChange = (model) => {
    const normalized = normalizeSortModel(model);

    // Prevent duplicate updates
    if (isSameSort(lastSortRef.current, normalized)) {
      return;
    }

    lastSortRef.current = normalized;
    onSortChange(normalized);
  };

  const handleExportButtonClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handlePrint = () => {
    setIsPrinting(true);
    handleMenuClose();
    try {
      if (apiRef?.current?.exportDataAsPrint) {
        const prevWidths = {};
        const printableCols = columnHeaders.filter((col) => col.field !== "actions");
        printableCols.forEach((col) => {
          const gridCol = apiRef.current.getColumn(col.field);
          if (gridCol) {
            prevWidths[col.field] = gridCol.computedWidth;
            apiRef.current.setColumnWidth(col.field, 100);
          }
        });

        apiRef.current.exportDataAsPrint({
          allColumns: true,
          fields: printableCols.map((col) => col.field),
          pageStyle: `
        @page { size: A3 landscape; margin: 5mm; }
        body { margin: 0; }
        table { width: 100%; border-collapse: collapse; margin: 0 auto; }
        .MuiDataGrid-root { width: 100%; margin: 0 auto !important; }`,
          hideToolbar: true,
          hideFooter: true,
        });

        setTimeout(() => {
          printableCols.forEach((col) => {
            const original = prevWidths[col.field];
            if (original) apiRef.current.setColumnWidth(col.field, original);
          });
          setIsPrinting(false);
        }, 1000);
      } else {
        setIsPrinting(false);
      }
    } catch (error) {
      setIsPrinting(false);
    }
  };

  const handleDownload = () => {
    handleMenuClose();
    if (apiRef?.current?.exportDataAsCsv) {
      const exportableCols = columnHeaders.filter((col) => col.field !== "actions");
      apiRef.current.exportDataAsCsv({
        allColumns: true,
        fields: exportableCols.map((col) => col.field),
      });
    }
  };

  const isExportDisabled = pdfSuccess && JSON.stringify(currentFilters) === JSON.stringify(prevFilters);

  const getOptionLabel = (options = [], value) => options.find((option) => option.value === value)?.label || "";

  const hasFilterValue = (filter) => {
    if (filter?.multiple) {
      return Array.isArray(filter?.value) && filter.value.length > 0;
    }
    return filter?.value !== null && filter?.value !== undefined && filter?.value !== "";
  };

  return (
    <>
      {(showFilterButton || showSearchBar || showExportButton || hasDropdownFilters || hasStartAction) && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            backgroundColor: "#F2ECF9",
            padding: "8px",
            borderRadius: "10px",
          }}
        >
          {hasStartAction && <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>{startAction}</Box>}

          {showFilterButton && (
            <FilterOptions
              selectedFilter={selectedFilter}
              filterOptions={filterOptions}
              onFilterChange={onFilterChange}
              disabled={disableFilterButton}
            />
          )}

          <Box sx={{ flexGrow: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
            {hasDropdownFilters &&
              dropdownFilters.map((filter) => {
                const selectedValues = Array.isArray(filter?.value) ? filter.value : [];
                if (filter.render) {
                  return (
                    <Box
                      key={filter.key}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        height: 40,
                      }}
                    >
                      <Box
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 40,
                            borderRadius: "10px",
                            backgroundColor: "#fff",
                            "& fieldset": {
                              borderRadius: "10px",
                            },
                          },
                          "& .MuiInputBase-root": {
                            height: 40,
                          },
                        }}
                      >
                        {filter.render()}
                      </Box>
                    </Box>
                  );
                }

                const selectedOptions = filter.multiple
                  ? filter.options?.filter((opt) => selectedValues.includes(opt.value))
                  : filter.options?.find((opt) => opt.value === filter.value) || null;

                return (
                  <Box key={filter.key} sx={{ minWidth: filter.minWidth || 180 }}>
                    <Autocomplete
                      multiple={Boolean(filter.multiple)}
                      options={filter.options || []}
                      value={selectedOptions}
                      loading={filter.loading}
                      disabled={Boolean(filter.disabled)}
                      disableCloseOnSelect={filter.multiple}
                      size="small"
                      clearOnEscape
                      slotProps={{
                        listbox: {
                          onScroll: (event) => {
                            const listbox = event.currentTarget;

                            if (listbox.scrollTop + listbox.clientHeight >= listbox.scrollHeight - 10) {
                              filter.onScrollEnd?.();
                            }
                          },
                        },
                      }}
                      isOptionEqualToValue={(option, value) => option.value === value.value}
                      getOptionLabel={(option) => option.label || ""}
                      onChange={(event, newValue) => {
                        if (filter.multiple) {
                          const values = newValue.map((v) => v.value);
                          filter.onChange?.(values, event);
                        } else {
                          filter.onChange?.(newValue?.value ?? "", event);
                        }
                      }}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          {filter.multiple && <Checkbox size="small" checked={selected} sx={{ mr: 1 }} />}
                          <ListItemText primary={option.label} />
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label={filter.label} placeholder={filter.placeholder || filter.label} />
                      )}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          backgroundColor: "#fff",
                        },
                      }}
                    />
                  </Box>
                );
              })}
            {showSearchBar && (
              <SearchBar
                placeholder={placeholder}
                searchText={searchText}
                onSearchChange={onSearchChange}
                disabled={disableSearchBar}
              />
            )}
            {showDateRangePicker && (
              <DateRangeInput
                startDate={dateRange?.startDate}
                endDate={dateRange?.endDate}
                onDateRangeChange={onDateRangeChange}
                style={{ ml: 1, width: 220 }}
              />
            )}
            {showExportButton && (
              <Box sx={{ ml: 1 }}>
                {onExportPdfClick && (
                  <Button
                    onClick={() => onExportPdfClick()}
                    disabled={isPdfGenerating || isExportDisabled}
                    variant="outlined"
                    sx={{
                      color: "#674BF5",
                      textTransform: "capitalize",
                      fontWeight: 500,
                      height: 38,
                      border: "1px solid #674BF5",
                      backgroundColor: "transparent",
                    }}
                  >
                    {isPdfGenerating ? (
                      <>
                        <CircularProgress size={16} sx={{ mr: 1, color: "#7D5DF7" }} />
                        Generating...
                      </>
                    ) : (
                      <>
                        <SimCardDownload sx={{ fontSize: 20, mr: 1 }} />
                        {buttonText ?? "Download PDF"}
                      </>
                    )}
                  </Button>
                )}

                {!onExportPdfClick && (
                  <>
                    <Button
                      onClick={handleExportButtonClick}
                      disabled={isPdfGenerating}
                      variant="outlined"
                      sx={{
                        color: "#674BF5",
                        textTransform: "capitalize",
                        fontWeight: 500,
                        height: 38,
                        border: "1px solid #674BF5",
                        backgroundColor: "transparent",
                      }}
                    >
                      {isPdfGenerating ? (
                        <>
                          <CircularProgress size={16} sx={{ mr: 1, color: "#7D5DF7" }} />
                          Generating...
                        </>
                      ) : (
                        <>
                          <FileUploadOutlined sx={{ fontSize: 16, mr: 1 }} />
                          {buttonText ?? "Export"}
                        </>
                      )}
                    </Button>

                    <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                      <MenuItem onClick={handlePrint}>Print</MenuItem>
                      <MenuItem onClick={handleDownload}>Download CSV</MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}

      <DataGrid
        apiRef={apiRef}
        rows={rows}
        columns={columnHeaders}
        autoHeight={autoHeight}
        loading={isPrinting ? false : loading}
        rowCount={rowCount}
        onRowClick={onRowClick}
        sortingMode="server"
        sortingOrder={["asc", "desc", null]}
        onSortModelChange={handleSortModelChange}
        sortModel={sortModel}
        disableColumnSorting={disableSorting}
        paginationModel={paginationModel}
        paginationMode="server"
        onPaginationModelChange={onPaginationChange}
        pageSizeOptions={[5, 10, 20, 50, 100]}
        rowHeight={38}
        columnHeaderHeight={50}
        disableColumnMenu
        hideFooterPagination={hideFooter}
        sx={{
          border: "none",
          "& .MuiDataGrid-cell:focus": {
            outline: "none",
          },
          "& .MuiDataGrid-cell": {
            color: "#5e5d72",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold",
            textTransform: "capitalize",
            color: "#5e5d72",
          },
          "& .MuiDataGrid-footerContainer": {
            minHeight: hideFooter ? "1px" : "auto",
            height: hideFooter ? "1px" : "auto",
            borderTop: "1px solid rgba(224, 224, 224, 1)",
            backgroundColor: "#fff",
            overflow: "hidden",
          },
          ...style,
        }}
      />
    </>
  );
};
